import React from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import DrawerComponent from "../../../components/Drawers/drawers";

import {
  ContentCopy,
  DoneAll,
  Clear,
  RemoveCircleOutline,
  LocalShipping,
  AssignmentTurnedIn,
  // Cancel,
  Block,
  Inventory,
} from "@mui/icons-material";
import inventory_icon from "../../../assets/icons/inventory.svg";
import clock_loader_10 from "../../../assets/icons/clock_loader_10.svg";


const RequestTimelineDrawer = ({ show, showDrawer, requestId }) => {
  const {
    stockRequestTimeline,
    stockRequestTimelineLoading,
    stockRequestTimelineError,
  } = useSelector((state) => state.storeManagement);

  const TimelineLoader = () => (
    <div className="animate-pulse w-full px-4 mt-4 mb-4">
      <div className="h-8 bg-gray-200 rounded-lg w-full mb-2"></div>
      <div className="h-6 bg-gray-200 rounded-lg w-3/4 mb-2"></div>
      <div className="h-6 bg-gray-200 rounded-lg w-1/2"></div>
    </div>
  );

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear().toString().slice(-2);
    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return {
      date: `${day} ${month} ${year}`,
      time: time,
    };
  };

  const handleCopyNumber = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`${text} copied successfully`);
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      DISPATCHED: "border-[#38AFE0] text-[#0066AF] bg-[#E7FBFE]",
      DELIVERED: "border-[#2B8C34] text-[#2B8C34] ",
      PROCESSING: "border-[#D4A407] text-[#D4A407] ",
      COMPLETED: "border-[#2B8C34] text-[#2B8C34] ",
      CANCELLED: "border-[#E75E63] bg-[#FEF0EC] text-[#B92020] ",
      PENDING: "border-[#D4A407] text-[#D4A407] ",
      STOCK_REQUEST: "border-[#E29700] text-[#E29700] bg-[#FFFFFF] ",
      FULFILLED: "border-[#72B278] text-[#2B8C34] bg-[#E1F7E3]",
      PART_APPROVED: "border-[#C2410C] text-[#C2410C] bg-[#FFFFFF]",
    };
    return styles[status] || "border-[#5F6D60] text-[#5F6D60] bg-[#F5F5F5]";
  };

  const getStatusIcon = (status) => {
    const iconProps = { style: { fontSize: 14 } };
    switch (status?.toUpperCase()) {
      case "DISPATCHED":
        return <LocalShipping {...iconProps} />;
      case "DELIVERED":
      case "COMPLETED":
        return <AssignmentTurnedIn {...iconProps} />;
      case "CANCELLED":
        return <Block {...iconProps} />;
      case "PROCESSING":
        return <Inventory {...iconProps} />;
      case "FULFILLED":
        return <img src={inventory_icon} alt="Fulfilled" {...iconProps} />;
      case "PART_APPROVED":
        return <RemoveCircleOutline {...iconProps} />;

      default:
        return null;
    }
  };

  return (
    <>
      <DrawerComponent
        title="Timeline"
        show={show}
        showDrawer={showDrawer}
        paddingAllowed={false}
      >
        <div>
          {stockRequestTimelineLoading ? (
            <>
              <TimelineLoader />
              <TimelineLoader />
              <TimelineLoader />
            </>
          ) : stockRequestTimelineError ? (
            <div className="px-4 py-6 text-[#B92020] text-sm">
              {stockRequestTimelineError}
            </div>
          ) : stockRequestTimeline.length > 0 ? (
            stockRequestTimeline.map((event, idx) => {
              const { date, time } = formatDateTime(event.created);

              // Map each event type to its design block
              switch (event.event_type) {
                case "DISPATCHED":
                  return (
                    <React.Fragment key={idx}>
                      <div className="flex justify-between items-center gap-2 px-4 py-2 border-b border-[#E1E6E1] bg-[#F6F7F6]">
                        <span
                          className={`px-2 py-1 rounded border ${getStatusBadgeStyle(
                            "DISPATCHED"
                          )} font-semibold text-xs flex items-center gap-1`}
                        >
                          {getStatusIcon("DISPATCHED")}
                          Dispatched
                        </span>
                        <span className="text-[#5F6D60] font-semibold text-xs">
                          {time} | {date}
                        </span>
                      </div>
                      <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-[#3E473F] font-semibold text-xs">
                            Stock Dispatched By:
                          </span>
                          <span className="text-[#3E473F] font-semibold text-xs">
                            {event.user?.full_name}
                          </span>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.user?.role || "Sales Admin"}
                          </span>
                        </div>
                        <div>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.location?.name}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 items-center px-4 py-2 border-b border-[#E1E6E1]">
                        <span className="text-[#344335] font-semibold text-xs">
                          Quantity Dispatched:
                        </span>
                        <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                          {event.event_details?.quantity_dispatched}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 items-center px-4 py-2 border-b border-[#E1E6E1]">
                        <span className="text-[#344335] font-semibold text-xs">
                          Delivery Method:
                        </span>
                        <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                          {event.event_details?.delivery_method}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 items-center px-4 py-2 border-b border-[#E1E6E1]">
                        <span className="text-[#344335] font-semibold text-xs">
                          Driver / Courier Details:
                        </span>
                        <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                          {event.event_details?.driver_courier_name}
                        </span>
                        <span className="text-[#5F6D60] text-sm">/</span>
                        <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] font-semibold px-2 py-1 rounded text-xs flex gap-1 items-center">
                          {event.event_details?.phone_number}
                          <ContentCopy
                            onClick={() =>
                              handleCopyNumber(
                                event.event_details?.phone_number
                              )
                            }
                            className="text-[#96A397] cursor-pointer"
                            style={{ fontSize: "16px" }}
                          />
                        </span>
                      </div>
                    </React.Fragment>
                  );
                case "APPROVED":
                  return (
                    <React.Fragment key={idx}>
                      <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                        <span className="bg-[#E1F7E3] text-[#2B8C34] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1">
                          <DoneAll style={{ fontSize: 16 }} />
                          Approved
                        </span>
                        <span className="text-[#5F6D60] font-semibold text-xs">
                          {time} | {date}
                        </span>
                      </div>
                      <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-[#344335] font-semibold text-xs">
                            Approved By:
                          </span>
                          <span className="text-[#344335] font-semibold text-xs">
                            {event.user?.full_name}
                          </span>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.user?.role || "Sales Admin"}
                          </span>
                        </div>
                        <div>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.location?.name}
                          </span>
                        </div>
                      </div>
                      <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                        <span className="text-[#344335] font-semibold text-xs">
                          Quantity Approved:
                        </span>
                        <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                          {event.event_details?.quantity_allocated}
                        </span>
                      </div>
                      {/* Donor Stores */}
                      {event.event_details?.donor_stores && (
                        <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                          <span className="text-[#344335] font-semibold text-xs">
                            Donor Store(s):
                          </span>
                          {event.event_details.donor_stores.map((store, i) => (
                            <React.Fragment key={i}>
                              <div className="flex items-center gap-1">
                                <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#F6F7F6] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                                  {store.location_name}
                                </span>
                                <span className="font-semibold text-lg">/</span>
                                <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                                  {store.quantity} Units
                                </span>
                              </div>
                              {i <
                                event.event_details.donor_stores.length - 1 && (
                                <div className="bg-[#E1E6E1] w-[1px] h-[20px]"></div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  );
                case "REJECTED":
                  return (
                    <React.Fragment key={idx}>
                      <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                        <span className="text-[#B92020] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1 border border-[#E75E63]">
                          <Clear style={{ fontSize: 16 }} />
                          Rejected
                        </span>
                        <span className="text-[#5F6D60] font-semibold text-xs">
                          {time} | {date}
                        </span>
                      </div>
                      <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-[#344335] font-semibold text-xs">
                            Rejected By:
                          </span>
                          <span className="text-[#344335] font-semibold text-xs">
                            {event.user?.full_name}
                          </span>
                          <span className="text-[#344335] px-2 py-1 font-semibold rounded text-xs">
                            {event.user?.role || "Sales Admin"}
                          </span>
                        </div>
                        <div>
                          <span className="px-2 py-1 font-semibold rounded text-xs text-[#344335]">
                            {event.location?.name}
                          </span>
                        </div>
                      </div>
                      {/* Reason for Rejection */}

                      <div className="flex flex-wrap gap-2 items-center px-4 py-2">
                        <span className="text-[#344335] font-semibold text-xs">
                          Reason for Rejection:
                        </span>

                        <span className="text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                          {event.event_details?.rejection_reason || "N/A"}
                        </span>
                      </div>

                      <div className="py-2 px-4 border-b border-[#E1E6E1]">
                        <div className="px-4 py-2 bg-[#F6F7F6] border border-[#E1E6E1] rounded-lg">
                          <p className="text-[#5F6D60] font-semibold text-xs">
                            Rejection Note:
                            <span className="text-[#79887B] font-medium ml-1">
                              {event.notes}
                            </span>
                          </p>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                case "FULFILLED":
                  return (
                    <React.Fragment key={idx}>
                      <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                        <span className="bg-[#E1F7E3] text-[#2B8C34] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1 border border-[#72B278]">
                          <AssignmentTurnedIn style={{ fontSize: 16 }} />
                          Fulfilled
                        </span>
                        <span className="text-[#5F6D60] font-semibold text-xs">
                          {time} | {date}
                        </span>
                      </div>
                      <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-[#344335] font-semibold text-xs">
                            Stock Confirmed By:
                          </span>
                          <span className="text-[#344335] font-semibold text-xs">
                            {event.user?.full_name}
                          </span>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.user?.role || "Sales Admin"}
                          </span>
                        </div>
                        <div>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.location?.name}
                          </span>
                        </div>
                      </div>
                      <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                        <span className="text-[#344335] font-semibold text-xs">
                          Quantity Confirmed:
                        </span>
                        <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                          {event.event_details?.quantity_confirmed}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                   case "PARTIALLY_FULFILLED":
                       return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="bg-[#FFEDD5] text-[#C2410C] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1 border border-[#F97316]">
                              <img alt="Part Fulfilled Icon" src={clock_loader_10} style={{ fontSize: 16 }} />
                              Part Fulfilled
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Stock Confirmed By:
                              </span>
                              <span className="text-[#344335] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Sales Admin"}
                              </span>
                            </div>
                            <div>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                          <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                            <span className="text-[#344335] font-semibold text-xs">
                              Quantity Confirmed:
                            </span>
                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                              {event.event_details?.quantity_confirmed}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                case "STOCK_REQUEST":
                  return (
                    <React.Fragment key={idx}>
                      <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                        <span
                          className={`px-2 py-1 rounded border ${getStatusBadgeStyle(
                            "STOCK_REQUEST"
                          )} font-semibold text-xs flex items-center gap-1`}
                        >
                          {getStatusIcon("STOCK_REQUEST")}
                          Stock Request
                        </span>
                        <span className="text-[#5F6D60] font-semibold text-xs">
                          {time} | {date}
                        </span>
                      </div>
                      <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                        <span className="text-[#344335] font-semibold text-xs">
                          Quantity:
                        </span>
                        <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                          {event.event_details?.quantity_requested}
                        </span>
                      </div>
                      <div className="px-4 py-2 flex border-b border-[#E1E6E1] flex-wrap gap-2 items-center">
                        <span className="text-[#344335] font-semibold text-xs">
                          Product Name:
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                            {event.event_details?.product_name}
                          </span>
                          <span className="font-semibold text-lg">/</span>
                          <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                            {event.event_details?.variant_data?.type}
                          </span>
                          <span className="font-semibold text-lg">/</span>
                          <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                            {event.event_details?.variant_data?.value}{" "}
                            {event.event_details?.variant_data?.unit}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between px-4 py-2 items-center">
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-[#344335] font-semibold text-xs">
                            Requested By:
                          </span>
                          <span className="text-[#344335] font-semibold text-xs">
                            {event.user?.full_name}
                          </span>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.user?.role || "Store Manager"}
                          </span>
                        </div>
                        <div>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.location?.name}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                // Add more cases for other event types as needed
                case "CANCELLED":
                  return (
                    <React.Fragment key={idx}>
                      <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                        <span
                          className={`px-2 py-1 rounded border ${getStatusBadgeStyle(
                            "CANCELLED"
                          )} font-semibold text-xs flex items-center gap-1`}
                        >
                          {getStatusIcon("CANCELLED")}
                          Cancelled
                        </span>
                        <span className="text-[#5F6D60] font-semibold text-xs">
                          {time} | {date}
                        </span>
                      </div>
                      <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="text-[#344335] font-semibold text-xs">
                            Cancelled By:
                          </span>
                          <span className="text-[#344335] font-semibold text-xs">
                            {event.user?.full_name}
                          </span>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.user?.role || "Sales Admin"}
                          </span>
                        </div>
                        <div>
                          <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                            {event.location?.name}
                          </span>
                        </div>
                      </div>
                      <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                        <span className="text-[#344335] font-semibold text-xs">
                          Reason for Cancellation:
                        </span>

                        <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                          {event.event_details?.cancellation_reason || "N/A"}
                        </span>
                      </div>
                      {event.notes && (
                        <div className="py-2 px-4 border-b border-[#E1E6E1]">
                          <div className="px-4 py-2 bg-[#F6F7F6] border border-[#E1E6E1] rounded-lg">
                            <p className="text-[#5F6D60] font-semibold text-xs">
                              Cancellation Note:
                              <span className="text-[#79887B] font-medium ml-1">
                                {event.notes}
                              </span>
                            </p>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );

                default:
                  return null;
              }
            })
          ) : (
            <div className="px-4 py-6 text-[#96A397] text-sm">
              No timeline events found.
            </div>
          )}
        </div>
      </DrawerComponent>
    </>
  );
};

export default RequestTimelineDrawer;
